import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useOffice } from '@/hooks/useOffice';

interface Tape {
  id: number;
  media_label: string;
  media_set: string | null;
  modified_date: string | null;
  tape_type: string | null;
  location: string | null;
  box_number: string | null;
  notes: string | null;
  is_new?: boolean;
}

export default function LibraryPage() {
  const { currentOfficeId } = useOffice();
  const [tapes, setTapes] = useState<Tape[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [lastRemoved, setLastRemoved] = useState<Tape[] | null>(null);
  const [singleRemove, setSingleRemove] = useState<Tape | null>(null);

  const load = async () => {
    if (!currentOfficeId) return;
    setLoading(true);
    setError('');
    try {
      const { data, error } = await supabase
        .from('tapes')
        .select('*')
        .eq('office_id', currentOfficeId)
        .eq('box_number', 'Backup Tape Library')
        .order('id', { ascending: true });
      if (error) throw error;
      setTapes(data as Tape[]);
      setSelected(new Set());
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [currentOfficeId]);

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const allSelected = useMemo(
    () => tapes.length > 0 && tapes.every((t) => selected.has(t.id)),
    [tapes, selected],
  );

  const sortedTapes = useMemo(() => {
    const sorted = [...tapes].sort((a, b) => {
      const la = (a.media_label || '').toLowerCase();
      const lb = (b.media_label || '').toLowerCase();
      if (la < lb) return sortDir === 'asc' ? -1 : 1;
      if (la > lb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [tapes, sortDir]);

  const toggleSort = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(tapes.map((t) => t.id)));
  };

  const removeTapes = async (ids: number[]) => {
    if (ids.length === 0) return;
    setRemoving(true);
    setError('');
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const todayStr = `${yyyy}-${mm}-${dd}`;
    try {
      const removedTapes = tapes.filter((t) => ids.includes(t.id));
      const { error } = await supabase
        .from('tapes')
        .update({ box_number: null, modified_date: todayStr, updated_at: new Date().toISOString(), is_new: false })
        .in('id', ids);
      if (error) throw error;

      setTapes((s) => s.filter((t) => !ids.includes(t.id)));
      setSelected(new Set());
      setConfirmOpen(false);
      setLastRemoved(removedTapes);
    } catch (e: any) {
      setError(e?.message || 'Failed to remove tapes');
    } finally {
      setRemoving(false);
    }
  };

  const removeSelected = () => {
    removeTapes(Array.from(selected));
  };

  const removeOne = (id: number) => {
    removeTapes([id]);
  };

  const undoRemove = async () => {
    if (!lastRemoved || lastRemoved.length === 0) return;
    setRemoving(true);
    setError('');
    const ids = lastRemoved.map((t) => t.id);
    try {
      await Promise.all(
        lastRemoved.map((t) =>
          supabase
            .from('tapes')
            .update({
              box_number: 'Backup Tape Library',
              modified_date: t.modified_date,
              is_new: t.is_new ?? false,
              updated_at: new Date().toISOString(),
            })
            .eq('id', t.id),
        ),
      );
      setTapes((s) => {
        const remaining = s.filter((t) => !ids.includes(t.id));
        return [...remaining, ...lastRemoved].sort((a, b) => a.id - b.id);
      });
      setLastRemoved(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to undo removal');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Backup Tape Library</h1>
          <p className="text-sm text-slate-500 mt-1">
            {tapes.length} tapes currently loaded in the tape machine
          </p>
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600">{selected.size} selected</span>
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={removing}
              className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50 cursor-pointer"
            >
              <i className="ri-delete-bin-6-line w-4 h-4 flex items-center justify-center" />
              {removing ? 'Removing...' : 'Remove Selected'}
            </button>
          </div>
        )}
      </div>

      <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-start gap-3">
        <i className="ri-information-line w-5 h-5 flex items-center justify-center shrink-0 mt-0.5" />
        <span>
          These tapes have <strong>Box Number = Backup Tape Library</strong>, meaning they are
          physically inside the tape drive right now, not stored in an archive box. Check the{' '}
          <strong>Remove</strong> box next to a tape — or use the <strong>select-all</strong>{' '}
          checkbox in the header — then click <strong>Remove Selected</strong> to move them out of
          the library.
        </span>
      </div>

      {lastRemoved && lastRemoved.length > 0 && (
        <div className="mb-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2">
            <i className="ri-arrow-go-back-line w-5 h-5 flex items-center justify-center shrink-0" />
            <span>
              Removed <strong>{lastRemoved.length}</strong> tape{lastRemoved.length > 1 ? 's' : ''} from
              the library.
            </span>
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={undoRemove}
              disabled={removing}
              className="whitespace-nowrap px-3 py-1.5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 cursor-pointer"
            >
              {removing ? 'Restoring...' : 'Undo'}
            </button>
            <button
              onClick={() => setLastRemoved(null)}
              disabled={removing}
              className="whitespace-nowrap p-1.5 rounded-md text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 cursor-pointer"
              title="Dismiss"
            >
              <i className="ri-close-line w-4 h-4 flex items-center justify-center" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={load} className="text-xs underline cursor-pointer whitespace-nowrap">
            Retry
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 bg-slate-50 sticky top-0 z-10 shadow-[0_1px_0_0_theme(colors.slate.200)]">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium rounded-tl-lg">
                <button
                  onClick={toggleSort}
                  className="inline-flex items-center gap-1 hover:text-slate-700 cursor-pointer whitespace-nowrap"
                  title="Sort by Media Label"
                >
                  Media Label
                  <i
                    className={
                      sortDir === 'asc'
                        ? 'ri-arrow-up-line w-4 h-4 flex items-center justify-center'
                        : 'ri-arrow-down-line w-4 h-4 flex items-center justify-center'
                    }
                  />
                </button>
              </th>
              <th className="text-left px-4 py-2.5 font-medium">Media Set</th>
              <th className="text-left px-4 py-2.5 font-medium">Modified</th>
              <th className="text-left px-4 py-2.5 font-medium">Tape Type</th>
              <th className="text-left px-4 py-2.5 font-medium">Location</th>
              <th className="text-left px-4 py-2.5 font-medium">Notes</th>
              <th className="text-center px-4 py-2.5 font-medium">
                <div className="inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={tapes.length === 0}
                    className="w-4 h-4 cursor-pointer accent-amber-600 disabled:cursor-not-allowed"
                    title="Select all tapes on this page"
                  />
                  <span className="whitespace-nowrap">Remove</span>
                </div>
              </th>
              <th className="text-center px-4 py-2.5 font-medium rounded-tr-lg">
                <span className="whitespace-nowrap">Undo</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="text-center py-10 text-slate-400 text-sm">
                  Loading...
                </td>
              </tr>
            )}
            {!loading &&
              sortedTapes.map((t) => (
                <tr
                  key={t.id}
                  className={
                    t.is_new
                      ? 'border-t border-emerald-100 bg-emerald-50'
                      : 'border-t border-slate-100 hover:bg-slate-50'
                  }
                >
                  <td className="px-4 py-2.5 font-medium font-mono">
                    <span className={t.is_new ? 'text-emerald-900' : ''}>{t.media_label}</span>
                    {t.is_new && (
                      <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-emerald-600 text-white align-middle">
                        <i className="ri-sparkling-line w-3 h-3 flex items-center justify-center" />
                        New
                      </span>
                    )}
                  </td>
                  <td className={`px-4 py-2.5 ${t.is_new ? 'text-emerald-700' : 'text-slate-600'}`}>
                    {t.media_set || '—'}
                  </td>
                  <td className={`px-4 py-2.5 text-xs ${t.is_new ? 'text-emerald-700' : 'text-slate-600'}`}>
                    {t.modified_date || '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    {t.tape_type ? (
                      <span
                        className={
                          t.is_new
                            ? 'inline-block px-2 py-0.5 text-xs rounded bg-emerald-100 text-emerald-700'
                            : 'inline-block px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-700'
                        }
                      >
                        {t.tape_type}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={`px-4 py-2.5 ${t.is_new ? 'text-emerald-700' : 'text-slate-600'}`}>
                    {t.location || '—'}
                  </td>
                  <td
                    className={`px-4 py-2.5 max-w-xs truncate ${t.is_new ? 'text-emerald-700' : 'text-slate-600'}`}
                    title={t.notes || ''}
                  >
                    {t.notes || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <input
                      type="checkbox"
                      checked={selected.has(t.id)}
                      onChange={() => toggleOne(t.id)}
                      className="w-4 h-4 cursor-pointer accent-amber-600"
                      title="Select this tape for removal"
                    />
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <button
                      onClick={() => setSingleRemove(t)}
                      disabled={removing}
                      title="Undo — remove this tape from the library"
                      className="w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:text-amber-600 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-go-back-line w-4 h-4 flex items-center justify-center" />
                    </button>
                  </td>
                </tr>
              ))}
            {!loading && tapes.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-10 text-slate-400 text-sm">
                  No tapes currently in the tape machine
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !removing && setConfirmOpen(false)}
          />
          <div className="relative w-full max-w-md bg-white rounded-lg border border-slate-200 p-6">
            <h3 className="text-lg font-semibold">Remove Tapes</h3>
            <p className="mt-2 text-sm text-slate-600">
              You are about to remove{' '}
              <strong className="text-slate-900">{selected.size}</strong> tape
              {selected.size > 1 ? 's' : ''} from the Tape Library. This will clear their box
              assignment and set the modified date to today. You can undo this right after removal.
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={removing}
                className="whitespace-nowrap px-4 py-2 rounded-md border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={removeSelected}
                disabled={removing}
                className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50 cursor-pointer"
              >
                <i className="ri-delete-bin-6-line w-4 h-4 flex items-center justify-center" />
                {removing ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {singleRemove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !removing && setSingleRemove(null)}
          />
          <div className="relative w-full max-w-md bg-white rounded-lg border border-slate-200 p-6">
            <h3 className="text-lg font-semibold">Remove Tape</h3>
            <p className="mt-2 text-sm text-slate-600">
              You are about to remove <strong className="text-slate-900">{singleRemove.media_label}</strong>{' '}
              from the Tape Library. This will clear its box assignment and set the modified date
              to today. You can undo this right after removal.
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setSingleRemove(null)}
                disabled={removing}
                className="whitespace-nowrap px-4 py-2 rounded-md border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  removeOne(singleRemove.id);
                  setSingleRemove(null);
                }}
                disabled={removing}
                className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50 cursor-pointer"
              >
                <i className="ri-delete-bin-6-line w-4 h-4 flex items-center justify-center" />
                {removing ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}